//core
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ActionReducerMapBuilder } from "@reduxjs/toolkit/src/mapBuilders";
import { NoInfer } from "@reduxjs/toolkit/src/tsHelpers";
import { setUser } from "../../../common/user";
import { agentStatisticReportService } from "../../../../services/admin/cashierStatisticReport.service";
import { setGeneralProgressHide, setGeneralProgressShow } from "../../../common/ui";

interface IObj {
  [i:string]: string;
}

export type GroupByObj = {
  [k: string]: string | IObj
}

type OptionsDataType = {
  groupBy: GroupByObj,
  rowOnPage: number[],
  separatorsForFormat: {
    csv: string[];
  },
  encodingTypes: string[];
  availableFilters: string[];
}

export type SummaryReportSliceState = {
  options: {
    data: OptionsDataType | null,
    error: any,
    isFetching: boolean,
  },
  list: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  columns: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  download: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  cashiers: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  currencies: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  brands: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  platforms: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
};

const initialState: SummaryReportSliceState = {
  options: {
    data: null,
    error: null,
    isFetching: false,
  },
  list: {
    data: null,
    error: null,
    isFetching: false,
  },
  columns: {
    data: null,
    error: null,
    isFetching: false,
  },
  download: {
    data: null,
    error: null,
    isFetching: false,
  },
  cashiers: {
    data: null,
    error: null,
    isFetching: false,
  },
  currencies: {
    data: null,
    error: null,
    isFetching: false,
  },
  brands: {
    data: null,
    error: null,
    isFetching: false,
  },
  platforms: {
    data: null,
    error: null,
    isFetching: false,
  },
};

export const getOptionsData: any = createAsyncThunk(
  'adm_cashier_statistic_report/getOptionsData',
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const response = await agentStatisticReportService.getOptions(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

export const getListData: any = createAsyncThunk(
  'adm_cashier_statistic_report/getListData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await agentStatisticReportService.getReport(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());
      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getCashiersData: any = createAsyncThunk(
  'adm_cashier_statistic_report/getCashiersData',
  async (payload: any, { rejectWithValue, fulfillWithValue, dispatch }) => {
    try {
      const response = await agentStatisticReportService.getCashiers(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        if (response.status === 404) {
          return fulfillWithValue({ list: [], totalEntries: 0 })
        }
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

export const getCurrenciesData: any = createAsyncThunk(
  'adm_cashier_statistic_report/getCurrenciesData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await agentStatisticReportService.getCurrencies(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

export const getBrandsData: any = createAsyncThunk(
  'adm_cashier_statistic_report/getBrandsData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await agentStatisticReportService.getBrands(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

export const getPlatformsData: any = createAsyncThunk(
  'adm_cashier_statistic_report/getPlatformsData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await agentStatisticReportService.getPlatforms(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        return rejectWithValue(data)
      }

      return data;
    } catch (error) {
      return rejectWithValue(error)
    }
  }
);

//slice
const agentStatisticReportSlice = createSlice({
  name: 'adm_cashier_statistic_report',
  initialState: initialState,
  reducers: {
    reset: () => initialState,
    clearListState(state) {
      state.list.data = null;
      state.list.error = null;
      state.list.isFetching = false;
    },
    clearColumnsState(state) {
      state.columns.data = null;
      state.columns.error = null;
      state.columns.isFetching = false;
    },
    clearDownload(state) {
      state.download.data = null;
      state.download.error = null;
      state.download.isFetching = false;
    },
    clearCashiersState(state) {
      state.cashiers.data = null;
      state.cashiers.error = null;
      state.cashiers.isFetching = false;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<SummaryReportSliceState>>) => {
    builder.addCase(getOptionsData.pending, (state) => {
      state.options.isFetching = true;
      state.options.error = null;
    });
    builder.addCase(getOptionsData.fulfilled, (state, action) => {
      state.options.error = null;
      state.options.isFetching = false;
      state.options.data = action.payload;
    });
    builder.addCase(getOptionsData.rejected, (state, action) => {
      state.options.isFetching = false;
      state.options.error = action.payload;
    });

    builder.addCase(getListData.pending, (state) => {
      state.list.isFetching = true;
      state.list.error = null;
    });
    builder.addCase(getListData.fulfilled, (state, action) => {
      state.list.error = null;
      state.list.isFetching = false;
      state.list.data = action.payload;
    });
    builder.addCase(getListData.rejected, (state, action) => {
      state.list.isFetching = false;
      state.list.error = action.payload;
    });

    builder.addCase(getCashiersData.pending, (state) => {
      state.cashiers.isFetching = true;
      state.cashiers.error = null;
    });
    builder.addCase(getCashiersData.fulfilled, (state, action) => {
      state.cashiers.error = null;
      state.cashiers.isFetching = false;
      state.cashiers.data = action.payload;
    });
    builder.addCase(getCashiersData.rejected, (state, action) => {
      state.cashiers.isFetching = false;
      state.cashiers.error = action.payload;
    });

    builder.addCase(getCurrenciesData.pending, (state) => {
      state.currencies.isFetching = true;
      state.currencies.error = null;
    });
    builder.addCase(getCurrenciesData.fulfilled, (state, action) => {
      state.currencies.error = null;
      state.currencies.isFetching = false;
      state.currencies.data = action.payload;
    });
    builder.addCase(getCurrenciesData.rejected, (state, action) => {
      state.currencies.isFetching = false;
      state.currencies.error = action.payload;
    });

    builder.addCase(getBrandsData.pending, (state) => {
      state.brands.isFetching = true;
      state.brands.error = null;
    });
    builder.addCase(getBrandsData.fulfilled, (state, action) => {
      state.brands.error = null;
      state.brands.isFetching = false;
      state.brands.data = action.payload;
    });
    builder.addCase(getBrandsData.rejected, (state, action) => {
      state.brands.isFetching = false;
      state.brands.error = action.payload;
    });

    builder.addCase(getPlatformsData.pending, (state) => {
      state.platforms.isFetching = true;
      state.platforms.error = null;
    });
    builder.addCase(getPlatformsData.fulfilled, (state, action) => {
      state.platforms.error = null;
      state.platforms.isFetching = false;
      state.platforms.data = action.payload;
    });
    builder.addCase(getPlatformsData.rejected, (state, action) => {
      state.platforms.isFetching = false;
      state.platforms.error = action.payload;
    });
  },
});

export default agentStatisticReportSlice.reducer;

export const {
  clearDownload,
  clearListState,
  clearColumnsState,
  clearCashiersState,
  reset,
} = agentStatisticReportSlice.actions;
